import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { bootstrapSilentRefresh } from '@/lib/api';

/** Max time to wait for the boot-time silent refresh before falling to
 *  Login. A hung /auth/refresh must never trap the driver on the splash.
 *  10s, not 4s: on weak garage signal a refresh plus /auth/me (and its one
 *  retry) can take longer than 4s, and falling to Login there threw away
 *  a valid cookie. */
const BOOTSTRAP_TIMEOUT_MS = 10_000;

/**
 * One-shot guard. Module-level (not a ref) so it survives React 18
 * StrictMode's double-invoked mount effect in dev — the boot refresh fires
 * at most once per page load and can't double-fire alongside the api.ts
 * reactive 401 interceptor. Resets naturally on the next full page load
 * (module re-init), which is exactly "app start".
 */
let bootstrapAttempted = false;

/**
 * Boot-time silent refresh. Run before the auth gate decides Login-vs-app.
 *
 * If there's no live session in localStorage (evicted by iOS Safari ITP /
 * storage pressure) but the durable 30-day httpOnly refresh cookie is still
 * valid, restore the session silently so the driver isn't bounced to Login.
 *
 * Returns whether the app should hold on a splash. Boot paths:
 *   - live localStorage session   → no splash, no refresh (returns false)
 *   - no session + valid cookie    → splash → /auth/refresh → setAuth → app
 *   - no session + no cookie / 401 → splash → falls through to Login
 *   - /auth/refresh hangs          → splash clears at the timeout → Login
 */
export function useBootstrapAuth(): boolean {
  const [isBootstrapping, setIsBootstrapping] = useState(
    () => !useAuthStore.getState().isAuthenticated && !bootstrapAttempted,
  );

  useEffect(() => {
    // Already authed (localStorage survived) or the boot refresh already ran
    // this load → nothing to do.
    if (useAuthStore.getState().isAuthenticated || bootstrapAttempted) {
      setIsBootstrapping(false);
      return;
    }
    bootstrapAttempted = true;

    const controller = new AbortController();
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      setIsBootstrapping(false);
    };

    // Timeout guard: never trap the user on the splash if the request hangs.
    // Aborting the in-flight refresh also prevents a late setAuth from
    // flipping Login back to the app after we've already fallen through.
    const timer = window.setTimeout(() => {
      controller.abort();
      finish();
    }, BOOTSTRAP_TIMEOUT_MS);

    void (async () => {
      try {
        await bootstrapSilentRefresh(controller.signal);
      } catch {
        // Any failure → fall to Login.
      } finally {
        window.clearTimeout(timer);
        finish();
      }
    })();

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return isBootstrapping;
}
